import { lazy, Suspense, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatPoint, openStreetMapLink, parsePoint, type GeoPoint } from './coordinates';

const LocationMap = lazy(() => import('./LocationMap'));

/**
 * Where something is, chosen by dropping a pin on an OpenStreetMap map.
 *
 * The pin is the way in for most people. The coordinates field beside it is the way in for
 * everyone else: a keyboard user, who cannot drag a pin, and the administrator who already has the
 * place open in Google Maps and can right-click it, copy "30.0074, 31.4913" and paste it here. The
 * two stay in step: dropping the pin rewrites the field, and typing a readable pair moves the pin.
 *
 * Controlled: the parent owns the point. The field's text is this component's own, because a
 * half-typed "30.0" is not a point yet and must not be thrown away; remount it (with a `key`) to
 * start again from the parent's value.
 */
export function LocationPicker({
  value,
  onChange,
  readOnly = false,
  error,
}: {
  readonly value: GeoPoint | null;
  readonly onChange?: (point: GeoPoint | null) => void;
  readonly readOnly?: boolean;
  /** A refusal from the form, such as a required pin that was not dropped. */
  readonly error?: string | null;
}) {
  const { t } = useTranslation();
  const inputId = useId();
  const hintId = useId();
  const [text, setText] = useState(() => (value ? formatPoint(value) : ''));

  const unreadable = text.trim() !== '' && parsePoint(text) === null;

  const pick = (point: GeoPoint) => {
    setText(formatPoint(point));
    onChange?.(point);
  };

  const type = (next: string) => {
    setText(next);
    onChange?.(next.trim() === '' ? null : parsePoint(next));
  };

  const clear = () => {
    setText('');
    onChange?.(null);
  };

  if (readOnly && value === null) {
    return <p className="field__hint">{t('location.notPlaced')}</p>;
  }

  return (
    <div className="location-picker">
      <Suspense fallback={<div className="location-map location-map--loading">{t('location.loading')}</div>}>
        <LocationMap
          value={value}
          onPick={readOnly ? undefined : pick}
          label={t('location.mapLabel')}
          pinTitle={t('location.pinTitle')}
        />
      </Suspense>

      {readOnly ? null : (
        <div className="location-picker__controls">
          <label className="field" htmlFor={inputId}>
            <span className="field__label">{t('location.coordinates')}</span>
            <input
              id={inputId}
              className="field__input numeric"
              dir="ltr"
              inputMode="decimal"
              autoComplete="off"
              spellCheck={false}
              placeholder="30.0444, 31.2357"
              value={text}
              aria-invalid={unreadable || Boolean(error)}
              aria-describedby={hintId}
              onChange={(event) => type(event.target.value)}
            />
          </label>
          {value ? (
            <button type="button" className="button button--ghost" onClick={clear}>
              {t('location.remove')}
            </button>
          ) : null}
        </div>
      )}

      {unreadable ? <p className="field__error">{t('location.unreadable')}</p> : null}
      {error ? (
        <p className="field__error" role="alert">
          {error}
        </p>
      ) : null}

      <p className="field__hint" id={hintId}>
        {readOnly ? null : `${t('location.hint')} `}
        {value ? (
          <a href={openStreetMapLink(value)} target="_blank" rel="noopener noreferrer">
            {t('location.openMap')}
          </a>
        ) : null}
      </p>
    </div>
  );
}
